import React from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import * as Flex from "../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { getCarImageFromConfig } from "../../../../../shared-logic/utils/typedUtils";
import type { ExtraType } from "../../../../../../common-deprecated/types/CommonTypes";
import * as Image from "../../../../../../common-deprecated/styles/toyota/Image";
import { BnbLabel, useBnbOptionalLabel } from "../../../utils/labels";
import Container from "../../../../../../common-deprecated/styles/v2/toyota/globals/Container";
import * as Styles from "./styles/Grade";
import * as Text from "../../../../../../common-deprecated/styles/v2/toyota/utilities/Text";
import * as Type from "../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import * as InlineDisclaimer from "../../../../../../common-deprecated/styles/v2/toyota/components/InlineDisclaimer";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getVisualForGradeImage } from "../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_GRADE_EQUIPMENT } from "../../../utils/modalConstants";
import { trackChangeModel, trackEngineViewEquipment } from "../../../utils/tracking";
import ButtonTertiary from "../../../../../../common-deprecated/components/aem/toyota/ButtonTertiary";
import ButtonQuaternary from "../../../../../../common-deprecated/components/aem/toyota/ButtonQuaternary";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const GradeInfo = (): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const grade = useBnbSelector((state) => state.grade);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const filterItems = useBnbSelector((state) => state.grade.filterItems);
    const filterEquipment = useBnbSelector((state) => state.commonSettings.query.filterEquipment);
    const isSelected = filterEquipment?.split(":")[0] === "true";

    const modelSpecificDisclaimer = useBnbOptionalLabel("modelSpecificEngineDisclaimer");

    const { model, bodyType, name, filterCar, colourCode } = grade;
    const { mainFeatureEquipment, description, listFeatureEquipment } = grade;

    const featureEquipment = mainFeatureEquipment.filter((equipment) => equipment.feature).slice(0, 4);

    const backgroundImage = model.defaultCCISBackground;

    const getImage = (width: number, height: number): string =>
        filterCar
            ? getCarImageFromConfig(
                  commonSettings,
                  "day-exterior",
                  4,
                  model.id,
                  filterCar.id,
                  { width, height, scaleMode: 0 },
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  filterCar.selectedOptions as ExtraType[],
              )
            : filterItems.length > 0
              ? getCarImageFromConfig(
                    commonSettings,
                    "day-exterior",
                    4,
                    model.id,
                    grade.defaultCarId,
                    { type: "jpg", width, height, scaleMode: 0 },
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    undefined,
                    false,
                    null,
                    false,
                    isSelected ? filterItems : [],
                )
              : getVisualForGradeImage(
                    commonSettings,
                    model.id,
                    grade.id,
                    bodyType.id,
                    {
                        type: "jpg",
                        width,
                        height,
                        bgImage: backgroundImage,
                        padding: backgroundImage ? "0,0,40,40" : "0",
                    },
                    backgroundImage ? 3 : 4,
                    colourCode,
                ).src;

    const modelStep = useStepUrl(NavStep.Model, undefined, undefined, () => trackChangeModel(NavStep.Engine));

    return (
        <Container mb={4} mt={{ xs: 4, lg: 5 }}>
            <Flex.Row>
                <Flex.Col mb={3}>
                    <ButtonQuaternary
                        hasIcon
                        aria-hidden="true"
                        tabIndex={-1}
                        href={modelStep.href}
                        onClick={modelStep.onClick}
                        aria-labelledby="label-change-grade"
                    >
                        <Icon variant="explore" />
                        <Text.Button id="label-change-grade">
                            <BnbLabel label="changeGrade" />
                        </Text.Button>
                    </ButtonQuaternary>
                </Flex.Col>
            </Flex.Row>

            <Flex.Row>
                <Flex.Col width={{ xs: 1, md: 1 / 2, lg: 3 / 12 }} textAlign={isRTL(commonSettings) ? "right" : "left"}>
                    <Type.H1 headingType="gamma" mb={1}>
                        <Type.ModelName mr={isRTL(commonSettings) ? 0 : 1} ml={isRTL(commonSettings) ? 1 : 0}>
                            {model.name}
                        </Type.ModelName>
                        <Type.GradeName>{name}</Type.GradeName>
                    </Type.H1>
                    <Type.BodyTypeName>{bodyType.name}</Type.BodyTypeName>

                    <Styles.ImageWrapperMobile width={{ xs: 1 }} flexDirection="row" mt={{ xs: 3 }}>
                        <Image.Fluid
                            src={getImage(720, 370)}
                            srcSet={`${getImage(720, 370)} 1x, ${getImage(1440, 740)} 2x`}
                            alt={`${model.name} ${name} ${bodyType.name}`}
                        />
                    </Styles.ImageWrapperMobile>
                </Flex.Col>
                <Styles.ImageWrapper width={{ xs: 1, md: 1 / 2, lg: 5 / 12 }} flexDirection="row" mt={{ xs: 3, md: 0 }}>
                    {/* TODO: Optimize the srcSet dimensions */}
                    <Image.Fluid
                        src={getImage(720, 370)}
                        srcSet={`${getImage(720, 370)} 1x, ${getImage(1440, backgroundImage ? 820 : 740)} 2x`}
                        alt={`${model.name} ${name} ${bodyType.name}`}
                    />
                </Styles.ImageWrapper>
                <Styles.GradeEquipmentInfo width={{ xs: 1, md: 1, lg: 4 / 12 }} mt={{ _: 3, lg: 0 }}>
                    {/* Standard equipment block*/}
                    {!featureEquipment.length && <span dangerouslySetInnerHTML={{ __html: description }} />}
                    {!!featureEquipment.length && (
                        <>
                            <Styles.GradeListTitle>
                                <BnbLabel label="standardEquipment" />
                            </Styles.GradeListTitle>
                            <Styles.GradeList role="list">
                                {featureEquipment.map((equipment) => (
                                    <li key={equipment.id}>
                                        {equipment.name}
                                        {!!equipment.toolTip && <Type.Strong pl={1}>{equipment.toolTip}</Type.Strong>}
                                    </li>
                                ))}
                            </Styles.GradeList>
                        </>
                    )}

                    {(mainFeatureEquipment.length > 0 || listFeatureEquipment.length > 0) && (
                        <ButtonTertiary
                            onClick={() => {
                                trackEngineViewEquipment();
                                dispatch(
                                    showModal({
                                        type: MODAL_GRADE_EQUIPMENT,
                                        gradeName: grade.name,
                                        gradeSubtitle: grade.subtitle,
                                        modelName: grade.model.name,
                                        mainFeatureEquipment: grade.mainFeatureEquipment,
                                        listFeatureEquipment: grade.listFeatureEquipment,
                                    }),
                                );
                            }}
                            aria-label={`${model.name} ${name} ${bodyType.name}`}
                        >
                            <BnbLabel label="showStandardEquipment" />
                        </ButtonTertiary>
                    )}

                    {/* WLTP disclaimers */}
                    {!!mainFeatureEquipment.length && (
                        <>
                            {mainFeatureEquipment.map(
                                (equipment) =>
                                    !!equipment.footNote && (
                                        <React.Fragment key={equipment.id}>
                                            <Type.SmallText color="grey4">{equipment.footNote}</Type.SmallText>
                                        </React.Fragment>
                                    ),
                            )}
                        </>
                    )}
                </Styles.GradeEquipmentInfo>
            </Flex.Row>
            {modelSpecificDisclaimer && (
                <InlineDisclaimer.Text mt={{ xs: 3 }}>{modelSpecificDisclaimer}</InlineDisclaimer.Text>
            )}
        </Container>
    );
};

export default GradeInfo;
